import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, ShoppingBag, CalendarDays, IndianRupee, Plus, Pencil, Trash2, X,
  ClipboardList, TrendingUp, ArrowUp, Upload, Download, FileSpreadsheet,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import VendorsAdmin from "@/components/VendorsAdmin";
import ExperiencesAdmin from "@/components/ExperiencesAdmin";
import CategoriesAdmin from "@/components/CategoriesAdmin";
import SourcingCategoriesAdmin from "@/components/SourcingCategoriesAdmin";
import BudgetTiersAdmin from "@/components/BudgetTiersAdmin";
import { OrdersAdmin } from "@/components/admin/OrdersAdmin";
import { ProfitDashboard } from "@/components/admin/ProfitDashboard";
import { ItemCostsAdmin } from "@/components/admin/ItemCostsAdmin";

const STATUSES = ["Pending", "Confirmed", "In Progress", "Completed", "Cancelled"];
const CATEGORIES = ["flowers", "cakes", "decorations", "hampers", "packages"];

const STATUS_STYLES = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Confirmed: "bg-sky-50 text-sky-700 border-sky-200",
  "In Progress": "bg-violet-50 text-violet-700 border-violet-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Admin() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "stats";

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Concierge Control</p>
        <h1 className="font-display text-3xl sm:text-4xl text-charcoal mt-1 capitalize">
          {tab === "stats" ? "Overview" : tab === "sourcing" ? "Sourcing Categories" : tab === "budget-tiers" ? "Budget Tiers" : tab === "profit" ? "Profit Dashboard" : tab === "costs" ? "Cost & Selling Prices" : tab}
        </h1>
      </div>
      {tab === "stats" && <Stats />}
      {tab === "bookings" && <BookingsAdmin />}
      {tab === "orders" && <OrdersAdmin />}
      {tab === "profit" && <ProfitDashboard />}
      {tab === "costs" && <ItemCostsAdmin />}
      {tab === "vendors" && <VendorsAdmin />}
      {tab === "categories" && <CategoriesAdmin />}
      {tab === "sourcing" && <SourcingCategoriesAdmin />}
      {tab === "budget-tiers" && <BudgetTiersAdmin />}
      {tab === "products" && <ProductsAdmin />}
      {tab === "experiences" && <ExperiencesAdmin />}
      {tab === "users" && <UsersAdmin />}
      {tab === "settings" && <SettingsAdmin />}
    </div>
  );
}

function SettingsAdmin() {
  const [fee, setFee] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [pricing, setPricing] = useState({
    standard_fee: "", express_fee: "", plan_ahead_discount: "", plan_ahead_min_days: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setFee(String(r.data.default_delivery_fee ?? 49));
      setWhatsapp(r.data.business_whatsapp || "");
      const p = r.data.delivery_pricing || {};
      setPricing({
        standard_fee: String(p.standard_fee ?? 199),
        express_fee: String(p.express_fee ?? 499),
        plan_ahead_discount: String(p.plan_ahead_discount ?? 99),
        plan_ahead_min_days: String(p.plan_ahead_min_days ?? 3),
      });
    }).finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const n = parseInt(fee, 10);
    if (Number.isNaN(n) || n < 0) { toast.error("Enter a valid non-negative number"); return; }
    const pf = {};
    for (const [k, v] of Object.entries(pricing)) {
      const num = parseInt(v, 10);
      if (Number.isNaN(num) || num < 0 || (k === "plan_ahead_min_days" && num < 1)) {
        toast.error("Enter valid numbers for all delivery pricing fields"); return;
      }
      pf[k] = num;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        default_delivery_fee: n,
        standard_delivery_fee: pf.standard_fee,
        express_delivery_fee: pf.express_fee,
        plan_ahead_discount: pf.plan_ahead_discount,
        plan_ahead_min_days: pf.plan_ahead_min_days,
        business_whatsapp: whatsapp,
      });
      toast.success("Settings saved — checkout pricing updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-charcoal/60">Loading…</div>;

  const PricingField = ({ label, hint, field, testId }) => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
        {label}
      </label>
      <input
        type="number" min={0} required value={pricing[field]}
        onChange={(e) => setPricing({ ...pricing, [field]: e.target.value })}
        className="w-40 rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
        data-testid={testId}
      />
      {hint && <p className="text-xs text-charcoal/50 mt-1">{hint}</p>}
    </div>
  );

  return (
    <form onSubmit={save} className="card-surface p-6 max-w-lg space-y-6" data-testid="settings-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Delivery</p>
        <h2 className="font-display text-2xl text-charcoal mt-1">Delivery pricing</h2>
        <p className="text-sm text-charcoal/60 mt-1">
          These values drive the checkout delivery fee and are enforced again by the backend at booking time.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PricingField label="Standard delivery fee (₹)" field="standard_fee" testId="settings-standard-fee"
          hint="2 days out, or morning/afternoon slot" />
        <PricingField label="Express delivery fee (₹)" field="express_fee" testId="settings-express-fee"
          hint="Same/next day + evening/night slot" />
        <PricingField label="Plan Ahead discount (₹)" field="plan_ahead_discount" testId="settings-plan-ahead-discount"
          hint="Off the Standard fee for early bookings" />
        <PricingField label="Plan Ahead min days" field="plan_ahead_min_days" testId="settings-plan-ahead-days"
          hint="Booked this many days ahead → discount applies" />
      </div>
      <div className="border-t border-warm pt-5">
        <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
          Business WhatsApp number
        </label>
        <input
          type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91 98765 43210"
          className="w-60 rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
          data-testid="settings-business-whatsapp"
        />
        <p className="text-xs text-charcoal/50 mt-1">Customers booking experiences or on-request items chat with this number to confirm the final price.</p>
      </div>
      <div className="border-t border-warm pt-5">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
            Legacy default delivery fee (₹)
          </label>
          <input
            type="number" min={0} required value={fee}
            onChange={(e) => setFee(e.target.value)}
            className="w-40 rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
            data-testid="settings-delivery-fee"
          />
          <p className="text-xs text-charcoal/50 mt-1">Used by older flows; the dynamic pricing above drives checkout.</p>
        </div>
      </div>
      <button type="submit" disabled={saving}
        className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
        data-testid="settings-save">
        {saving ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-charcoal/60">Loading…</div>;
  const cards = [
    { label: "Total users", value: s.users, icon: Users, tint: "bg-violet-100 text-violet-600" },
    { label: "Total bookings", value: s.total_bookings, icon: ShoppingBag, tint: "bg-rose-100 text-rose-500" },
    { label: "Today's bookings", value: s.today_bookings, icon: CalendarDays, tint: "bg-amber-100 text-amber-600" },
    { label: "Pending", value: s.pending_bookings, icon: ClipboardList, tint: "bg-sky-100 text-sky-600" },
    { label: "Revenue (₹)", value: s.revenue, icon: IndianRupee, tint: "bg-emerald-100 text-emerald-600" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((c, i) => (
        <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
          className="card-surface p-5" data-testid={`stat-${c.label.toLowerCase().replace(/[^\w]/g, '-')}`}>
          <div className={`w-10 h-10 rounded-2xl ${c.tint} flex items-center justify-center mb-3`}>
            <c.icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <p className="font-display text-3xl text-charcoal">{typeof c.value === 'number' ? c.value.toLocaleString('en-IN') : c.value}</p>
          <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-charcoal/50 mt-1">{c.label}</p>
        </motion.div>
      ))}
    </div>
  );
}

function BookingsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/bookings${filter ? `?status=${encodeURIComponent(filter)}` : ""}`).then((r) => setItems(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, changes) => {
    try {
      await api.patch(`/admin/bookings/${id}`, changes);
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter("")} className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition ${filter === "" ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm hover:border-charcoal"}`} data-testid="filter-all">All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition ${filter === s ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm hover:border-charcoal"}`}
            data-testid={`filter-${s}`}>{s}</button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="card-surface p-10 text-center text-charcoal/60" data-testid="admin-bookings-empty">No bookings.</div>
      ) : items.map((b, i) => (
        <div key={b.id} className="card-surface p-5 sm:p-6" data-testid={`admin-booking-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-500">{b.id}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[b.status]}`}>{b.status}</span>
              </div>
              <p className="font-display text-lg sm:text-xl text-charcoal mt-1">{b.user_name} · <span className="text-charcoal/70 text-sm sm:text-base">{b.user_email}</span></p>
              <p className="text-sm text-charcoal/60 mt-1">{b.date} · {b.time_slot} · {b.address}</p>
              <p className="text-sm text-charcoal/60">Phone: {b.phone}</p>
              {b.occasion && <p className="text-sm text-charcoal/60">Occasion: {b.occasion}</p>}
            </div>
            <p className="font-display text-2xl text-charcoal whitespace-nowrap">₹{b.total?.toLocaleString('en-IN')}</p>
          </div>
          <ul className="mt-4 text-sm text-charcoal/80">
            {b.items.map((it, idx) => (
              <li key={idx} className="flex justify-between border-b border-warm py-1.5">
                <span>{it.name} × {it.quantity}</span><span>₹{(it.price * it.quantity).toLocaleString('en-IN')}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <select value={b.status} onChange={(e) => update(b.id, { status: e.target.value })} data-testid={`booking-status-select-${i}`}
              className="rounded-xl bg-white border border-warm px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Assign vendor" defaultValue={b.assigned_vendor || ""} data-testid={`assign-vendor-${i}`}
              onBlur={(e) => e.target.value !== (b.assigned_vendor || "") && update(b.id, { assigned_vendor: e.target.value })}
              className="rounded-xl bg-white border border-warm px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none" />
            <input placeholder="Note to customer" defaultValue={b.vendor_note || ""} data-testid={`vendor-note-${i}`}
              onBlur={(e) => e.target.value !== (b.vendor_note || "") && update(b.id, { vendor_note: e.target.value })}
              className="rounded-xl bg-white border border-warm px-4 py-2.5 text-sm focus:border-rose-400 focus:outline-none" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [category, setCategory] = useState("");
  const [view, setView] = useState("grid");
  const [categories, setCategories] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/products${category ? `?category=${category}` : ""}`).then((r) => setItems(r.data));
  }, [category]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, [showDialog]);

  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCategory("")} className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition ${category === "" ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm hover:border-charcoal"}`} data-testid="pcat-all">All</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setCategory(c.slug)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition capitalize ${category === c.slug ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm hover:border-charcoal"}`}
              data-testid={`pcat-${c.slug}`}>{c.name}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-warm bg-white p-0.5">
            <button onClick={() => setView("grid")} data-testid="view-grid"
              className={`rounded-full px-3 py-1 text-xs font-semibold ${view === "grid" ? "bg-charcoal text-white" : "text-charcoal/60"}`}>Grid</button>
            <button onClick={() => setView("table")} data-testid="view-table"
              className={`rounded-full px-3 py-1 text-xs font-semibold ${view === "table" ? "bg-charcoal text-white" : "text-charcoal/60"}`}>Table</button>
          </div>
          <button onClick={() => setShowImport(true)}
            className="rounded-full px-4 py-2.5 text-sm font-bold text-charcoal bg-white border border-warm hover:border-charcoal transition flex items-center gap-2"
            data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" strokeWidth={2.25} /> Bulk import
          </button>
          <button onClick={() => { setEditing(null); setShowDialog(true); }}
            className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="add-product-btn">
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Add product
          </button>
        </div>
      </div>

      {view === "table" ? (
        <div className="card-surface overflow-hidden" data-testid="products-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-linen-2">
                <tr>
                  <th className="text-left p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Product</th>
                  <th className="text-left p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Category</th>
                  <th className="text-left p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Vendor</th>
                  <th className="text-left p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Pincode</th>
                  <th className="text-right p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Price</th>
                  <th className="text-left p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Status</th>
                  <th className="text-right p-3 font-semibold text-charcoal/60 uppercase tracking-widest text-[10px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p, i) => (
                  <tr key={p.id} className="border-t border-warm hover:bg-linen-2/50" data-testid={`prod-row-${i}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        <span className="font-medium text-charcoal">{p.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-charcoal/70 capitalize">{p.category}</td>
                    <td className="p-3 text-charcoal/70">{p.vendor_shop_name || <span className="text-charcoal/30">—</span>}</td>
                    <td className="p-3 text-charcoal/70">{p.delivery_pincode || <span className="text-charcoal/30">—</span>}</td>
                    <td className="p-3 text-right font-semibold text-charcoal">₹{p.price?.toLocaleString('en-IN')}</td>
                    <td className="p-3">
                      {p.label ? (
                        <span className="rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">{p.label}</span>
                      ) : p.available ? (
                        <span className="rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Available</span>
                      ) : (
                        <span className="rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Unavailable</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => { setEditing(p); setShowDialog(true); }} className="p-1.5 rounded-full hover:bg-linen-2" data-testid={`row-edit-${i}`}>
                        <Pencil className="w-3.5 h-3.5 text-charcoal/60" strokeWidth={1.75} />
                      </button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded-full hover:bg-rose-50" data-testid={`row-del-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.75} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p, i) => (
            <div key={p.id} className="card-surface overflow-hidden" data-testid={`admin-product-${i}`}>
              <div className="relative">
                <img src={p.image_url} className="w-full aspect-video object-cover" alt="" />
                {p.label && (
                  <span className="absolute top-2 left-2 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest">{p.label}</span>
                )}
              </div>
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-500 capitalize">{p.category}</p>
                <p className="font-display text-lg text-charcoal mt-1">{p.name}</p>
                <p className="text-xs text-charcoal/60">{p.vendor_shop_name || "Unassigned vendor"} {p.delivery_pincode ? `· ${p.delivery_pincode}` : ""}</p>
                <p className="text-sm text-charcoal/60 line-clamp-2 mt-1">{p.description}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-display text-xl text-charcoal">₹{p.price?.toLocaleString('en-IN')}</span>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(p); setShowDialog(true); }} className="p-2 rounded-full hover:bg-linen-2" data-testid={`edit-prod-${i}`}><Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.75} /></button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`del-prod-${i}`}><Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.75} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDialog && (
        <ProductDialog initial={editing} onClose={() => setShowDialog(false)} onSaved={() => { setShowDialog(false); load(); }} />
      )}
      {showImport && (
        <BulkImportDialog onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); load(); }} />
      )}
    </div>
  );
}

function BulkImportDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Template download failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/products/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(`Imported ${res.data.created} product${res.data.created === 1 ? "" : "s"}`);
      }
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} row${res.data.failed === 1 ? "" : "s"} failed — see details`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const done = () => { onImported(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
      onClick={onClose} data-testid="bulk-import-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Bulk import</p>
            <p className="font-display text-2xl text-charcoal mt-1">Import products from CSV</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2" data-testid="bulk-import-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div className="rounded-2xl bg-linen-2 p-5 space-y-3">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="w-5 h-5 text-charcoal/70 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="text-sm text-charcoal/80 space-y-1">
                <p className="font-semibold text-charcoal">1. Download the template.</p>
                <p className="text-charcoal/60">Includes required columns and two example rows (a global flower bouquet and a pincode-restricted cake).</p>
              </div>
            </div>
            <button type="button" onClick={downloadTemplate}
              className="rounded-full px-4 py-2 text-xs font-bold bg-white border border-warm hover:border-charcoal transition flex items-center gap-2"
              data-testid="download-template-btn">
              <Download className="w-4 h-4" strokeWidth={2.25} /> Download products_template.csv
            </button>
          </div>

          <div className="rounded-2xl bg-linen-2 p-5 space-y-3">
            <p className="font-semibold text-charcoal text-sm">2. Fill it in.</p>
            <ul className="text-xs text-charcoal/60 space-y-1 list-disc pl-5">
              <li><b>images</b>: at least 3 URLs, separated by <code className="bg-white px-1 rounded">|</code></li>
              <li><b>tags</b>, <b>flavors</b>, <b>available_pincodes</b>: pipe-separated</li>
              <li><b>weight_variants</b> / <b>variants</b>: <code className="bg-white px-1 rounded">Label:Price|Label:Price</code></li>
              <li><b>is_global</b>: <code className="bg-white px-1 rounded">true</code> or <code className="bg-white px-1 rounded">false</code> — pincodes required if false</li>
              <li><b>delivery_fee_type</b>: <code className="bg-white px-1 rounded">free</code>, <code className="bg-white px-1 rounded">standard</code>, or <code className="bg-white px-1 rounded">custom</code></li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-charcoal text-sm mb-2">3. Upload the filled CSV.</p>
            <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-warm hover:border-rose-400 transition p-6 text-center bg-white"
              data-testid="csv-drop-zone">
              <Upload className="w-6 h-6 mx-auto text-charcoal/40 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-charcoal/70">
                {file ? <span className="font-semibold text-charcoal">{file.name}</span> : "Click to choose a .csv file"}
              </p>
              <input type="file" accept=".csv,text/csv" className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                data-testid="csv-file-input" />
            </label>
          </div>

          {result && (
            <div className="rounded-2xl border border-warm p-5 space-y-3" data-testid="import-result">
              <div className="grid grid-cols-3 gap-3">
                <ResultStat label="Rows" value={result.total} tint="bg-linen-2 text-charcoal" />
                <ResultStat label="Created" value={result.created} tint="bg-emerald-50 text-emerald-700" icon={CheckCircle2} />
                <ResultStat label="Failed" value={result.failed} tint="bg-rose-50 text-rose-700" icon={AlertCircle} />
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-56 overflow-y-auto text-xs">
                  <p className="font-semibold text-charcoal/70 mb-2 uppercase tracking-wider text-[10px]">Errors</p>
                  <ul className="space-y-1.5">
                    {result.errors.map((er, i) => (
                      <li key={i} className="flex gap-2 text-charcoal/80" data-testid={`import-error-${i}`}>
                        <span className="font-mono text-charcoal/50 flex-shrink-0">row {er.row}</span>
                        <span className="truncate">{er.name && <b className="text-charcoal">{er.name}: </b>}{er.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="p-6 border-t border-warm flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          {result ? (
            <button type="button" onClick={done}
              className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40"
              style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
              data-testid="import-done-btn">
              Done
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose}
                className="rounded-full px-5 py-3 text-sm font-bold text-charcoal bg-white border border-warm hover:border-charcoal transition">
                Cancel
              </button>
              <button type="submit" disabled={!file || uploading}
                className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50 flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
                data-testid="import-submit-btn">
                <Upload className="w-4 h-4" strokeWidth={2.25} />
                {uploading ? "Importing…" : "Import"}
              </button>
            </>
          )}
        </footer>
      </form>
    </div>
  );
}

function ResultStat({ label, value, tint, icon: Icon }) {
  return (
    <div className={`rounded-2xl ${tint} p-4 flex items-center gap-3`}>
      {Icon && <Icon className="w-5 h-5" strokeWidth={2} />}
      <div>
        <p className="font-display text-2xl leading-none">{value}</p>
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-70 mt-1">{label}</p>
      </div>
    </div>
  );
}

function ProductDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "",
    description: initial?.description || "",
    price: initial?.price || 999,
    images: initial?.images && initial.images.length ? initial.images : (initial?.image_url ? [initial.image_url] : []),
    available: initial?.available !== false,
    tags: (initial?.tags || []).join(", "),
    vendor_id: initial?.vendor_id || "",
    delivery_pincode: initial?.delivery_pincode || "",
    label: initial?.label || "",
    is_global: initial ? !!initial?.is_global : true,
    available_pincodes: initial?.available_pincodes || [],
    delivery_fee_type: initial?.delivery_fee_type || "free",
    delivery_fee_amount: initial?.delivery_fee_amount ?? 0,
    product_type: initial?.product_type || "standard",
    weight_variants: initial?.weight_variants || [],
    flavors: initial?.flavors || [],
    variants: initial?.variants || [],
    min_lead_time_days: initial?.min_lead_time_days ?? 0,
    stock_status: initial?.stock_status || "available",
    // Merchandising layer
    is_trending: initial?.is_trending || false,
    trend_priority: initial?.trend_priority ?? 0,
    trend_start_date: initial?.trend_start_date || "",
    trend_end_date: initial?.trend_end_date || "",
    is_featured: initial?.is_featured || false,
    is_new: initial?.is_new || false,
    relevant_occasions: initial?.relevant_occasions || [],
    relevant_relationships: (initial?.relevant_relationships || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newFlavor, setNewFlavor] = useState("");

  useEffect(() => {
    api.get("/admin/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/admin/categories").then((r) => {
      setCategories(r.data || []);
      // Default the category to first available if creating and unset.
      if (!initial && !form.category && r.data?.length) {
        setForm((f) => ({ ...f, category: r.data[0].slug }));
      }
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.images.length < 3) {
      toast.error("Please upload at least 3 product images");
      return;
    }
    if (form.product_type === "cake" && form.weight_variants.length === 0) {
      toast.error("Add at least one weight variant for cake products");
      return;
    }
    if (!form.is_global && form.available_pincodes.length === 0) {
      toast.error("Add at least one pincode, or switch availability to Global");
      return;
    }
    if (form.delivery_fee_type === "custom" && (!form.delivery_fee_amount || parseInt(form.delivery_fee_amount, 10) < 0)) {
      toast.error("Enter a valid custom delivery fee amount");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        image_url: form.images[0] || "",
        price: parseInt(form.price, 10),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        vendor_id: form.vendor_id || null,
        delivery_pincode: form.delivery_pincode || null,
        label: form.label || null,
        min_lead_time_days: parseInt(form.min_lead_time_days, 10) || 0,
        available_pincodes: form.is_global ? [] : form.available_pincodes,
        delivery_fee_amount: form.delivery_fee_type === "custom" ? parseInt(form.delivery_fee_amount, 10) || 0 : 0,
        weight_variants: form.weight_variants.map((v) => ({
          weight_label: (v.weight_label || "").trim(),
          price: parseInt(v.price, 10) || 0,
        })).filter((v) => v.weight_label),
        variants: form.variants.map((v) => ({
          option_label: (v.option_label || "").trim(),
          price: parseInt(v.price, 10) || 0,
        })).filter((v) => v.option_label),
        flavors: form.flavors.map((f) => (f || "").trim()).filter(Boolean),
        is_trending: !!form.is_trending,
        trend_priority: parseInt(form.trend_priority, 10) || 0,
        trend_start_date: form.trend_start_date || null,
        trend_end_date: form.trend_end_date || null,
        is_featured: !!form.is_featured,
        is_new: !!form.is_new,
        relevant_occasions: form.relevant_occasions,
        relevant_relationships: form.relevant_relationships.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (initial) await api.patch(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const isCake = form.product_type === "cake";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm" onClick={onClose} data-testid="product-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between sticky top-0 bg-white z-10">
          <p className="font-display text-2xl text-charcoal">{initial ? "Edit product" : "Add product"}</p>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2"><X className="w-5 h-5" /></button>
        </header>

        <div className="p-6 space-y-6">
          {/* ---- Basic ---- */}
          <Section title="Basics">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Name *"><Inp value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="pf-name" /></F>
              <F label="Category *">
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-category">
                  <option value="" disabled>Select category…</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </F>
              <F label="Product type *">
                <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value, weight_variants: [], variants: [], flavors: [] })}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-type">
                  <option value="standard">Standard</option>
                  <option value="cake">Cake</option>
                  <option value="flowers">Flowers</option>
                  <option value="other">Other</option>
                </select>
              </F>
              <F label="Base price (₹) *"><Inp type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required testId="pf-price" /></F>
              <F label="Vendor">
                <select value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-vendor">
                  <option value="">— Unassigned —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.shop_name}</option>)}
                </select>
              </F>
              <F label="Delivery pincode"><Inp value={form.delivery_pincode} onChange={(v) => setForm({ ...form, delivery_pincode: v })} testId="pf-pincode" placeholder="302001" /></F>
              <F label="Minimum lead time (days)">
                <Inp type="number" value={form.min_lead_time_days} onChange={(v) => setForm({ ...form, min_lead_time_days: v })} testId="pf-lead-time" />
              </F>
              <F label="Stock status *">
                <select value={form.stock_status} onChange={(e) => setForm({ ...form, stock_status: e.target.value })}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-stock">
                  <option value="available">Available</option>
                  <option value="coming_soon">Coming Soon</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </F>
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="pf-available"
                  className="w-4 h-4 rounded accent-rose-500" />
                <span className="text-sm text-charcoal/80">Available for booking</span>
              </label>
            </div>
          </Section>

          {/* ---- Availability (visibility by pincode) ---- */}
          <Section title="Availability">
            <div className="space-y-3">
              <RadioRow
                name="availability"
                value={form.is_global ? "global" : "specific"}
                onChange={(v) => setForm({ ...form, is_global: v === "global" })}
                options={[
                  { value: "global", label: "Global", hint: "Orderable from any pincode" },
                  { value: "specific", label: "Specific Pincodes Only", hint: "Only listed pincodes below" },
                ]}
                testKey="pf-availability"
              />
              {!form.is_global && (
                <PincodeListEditor
                  pincodes={form.available_pincodes}
                  onChange={(list) => setForm({ ...form, available_pincodes: list })}
                />
              )}
            </div>
          </Section>

          {/* ---- Delivery Fee (independent of availability) ---- */}
          <Section title="Delivery Fee">
            <RadioRow
              name="delivery_fee"
              value={form.delivery_fee_type}
              onChange={(v) => setForm({ ...form, delivery_fee_type: v })}
              options={[
                { value: "free", label: "Free Delivery", hint: "No delivery charge" },
                { value: "standard", label: "Standard (₹49)", hint: "Uses the admin default fee" },
                { value: "custom", label: "Custom Amount", hint: "Set a specific ₹ amount below" },
              ]}
              testKey="pf-fee-type"
            />
            {form.delivery_fee_type === "custom" && (
              <div className="mt-3 max-w-xs">
                <F label="Custom delivery fee (₹)">
                  <Inp type="number" value={form.delivery_fee_amount}
                    onChange={(v) => setForm({ ...form, delivery_fee_amount: v })}
                    testId="pf-fee-amount" placeholder="e.g. 199" />
                </F>
              </div>
            )}
          </Section>

          {/* ---- Images ---- */}
          <Section title={`Images * (minimum 3, currently ${form.images.length})`}>
            <MultiImageUpload images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} />
          </Section>

          {/* ---- Description ---- */}
          <Section title="Full description">
            <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 resize-none focus:outline-none focus:border-rose-400" data-testid="pf-description"
              placeholder="Tell the customer what makes this product special…" />
            <div className="mt-3">
              <F label="Tags (comma separated)"><Inp value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testId="pf-tags" placeholder="12 red roses, hand-tied, gift-wrapped" /></F>
            </div>
          </Section>

          {/* ---- Cake-only ---- */}
          {isCake && (
            <>
              <Section title="Weight variants * (cake)">
                <WeightVariantsTable
                  rows={form.weight_variants}
                  onChange={(rows) => setForm({ ...form, weight_variants: rows })}
                />
                <p className="text-xs text-charcoal/50 mt-2">Add each weight option customers can choose (0.5kg, 1kg, custom). Price is per weight.</p>
              </Section>
              <Section title="Flavors (cake)">
                <FlavorsEditor
                  flavors={form.flavors}
                  onAdd={(f) => setForm({ ...form, flavors: [...form.flavors, f] })}
                  onRemove={(i) => setForm({ ...form, flavors: form.flavors.filter((_, j) => j !== i) })}
                  newFlavor={newFlavor}
                  setNewFlavor={setNewFlavor}
                />
                <p className="text-xs text-charcoal/50 mt-2">Flavor selection doesn&apos;t change price.</p>
              </Section>
            </>
          )}

          {/* ---- Non-cake variants ---- */}
          {!isCake && (
            <Section title="Variants (size / color / etc.)">
              <VariantsTable
                rows={form.variants}
                onChange={(rows) => setForm({ ...form, variants: rows })}
              />
              <p className="text-xs text-charcoal/50 mt-2">Optional. Leave empty if the product has no variants.</p>
            </Section>
          )}

          {/* ---- Merchandising (Trending / Featured) ---- */}
          <Section title="Merchandising">
            <p className="text-xs text-charcoal/50 -mt-1 mb-3">
              Promotion only — never overrides category, product-type, semantic, budget or pincode rules in the AI planner.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_trending} onChange={(e) => setForm({ ...form, is_trending: e.target.checked })}
                  className="w-4 h-4 rounded accent-rose-500" data-testid="pf-is-trending" />
                <span className="text-sm text-charcoal/80">Trending</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                  className="w-4 h-4 rounded accent-rose-500" data-testid="pf-is-featured" />
                <span className="text-sm text-charcoal/80">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
                  className="w-4 h-4 rounded accent-rose-500" data-testid="pf-is-new" />
                <span className="text-sm text-charcoal/80">New arrival</span>
              </label>
            </div>
            {form.is_trending && (
              <div className="grid sm:grid-cols-3 gap-4 mt-4" data-testid="pf-trending-fields">
                <F label="Trend priority">
                  <Inp type="number" value={form.trend_priority} onChange={(v) => setForm({ ...form, trend_priority: v })} testId="pf-trend-priority" />
                </F>
                <F label="Trend start (optional)">
                  <input type="date" value={form.trend_start_date} onChange={(e) => setForm({ ...form, trend_start_date: e.target.value })}
                    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-trend-start" />
                </F>
                <F label="Trend end (optional)">
                  <input type="date" value={form.trend_end_date} onChange={(e) => setForm({ ...form, trend_end_date: e.target.value })}
                    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-trend-end" />
                </F>
              </div>
            )}
            <div className="mt-4 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">Relevant occasions (empty = all)</p>
                <div className="flex flex-wrap gap-2">
                  {["Birthday", "Anniversary", "Other"].map((occ) => {
                    const on = form.relevant_occasions.includes(occ);
                    return (
                      <button key={occ} type="button"
                        onClick={() => setForm({
                          ...form,
                          relevant_occasions: on
                            ? form.relevant_occasions.filter((o) => o !== occ)
                            : [...form.relevant_occasions, occ],
                        })}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold border-2 transition-colors ${
                          on ? "border-rose-500 bg-rose-50 text-rose-600" : "border-warm text-charcoal/60 hover:border-charcoal/30"
                        }`}
                        data-testid={`pf-occasion-${occ.toLowerCase()}`}>
                        {occ}
                      </button>
                    );
                  })}
                </div>
              </div>
              <F label="Relevant relationships (comma separated, empty = all)">
                <Inp value={form.relevant_relationships} onChange={(v) => setForm({ ...form, relevant_relationships: v })}
                  testId="pf-relationships" placeholder="e.g. partner, wife, mom" />
              </F>
            </div>
          </Section>

          {/* ---- Legacy label (keeps AI planner filter working) ---- */}
          <Section title="Legacy label (advanced)">
            <F label="Availability label">
              <select value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="pf-label">
                <option value="">— None —</option>
                <option value="Coming Soon">Coming Soon</option>
                <option value="Inspiration Only">Inspiration Only</option>
              </select>
            </F>
          </Section>
        </div>

        <footer className="p-6 border-t border-warm flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary text-sm" data-testid="pf-cancel">Cancel</button>
          <button type="submit" disabled={saving}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="pf-save">{saving ? "Saving…" : "Save"}</button>
        </footer>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500 mb-3">{title}</p>
    {children}
  </div>
);
const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">{label}</label>{children}</div>;
const Inp = ({ value, onChange, type = "text", required, testId, placeholder }) => (
  <input type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid={testId} />
);

function WeightVariantsTable({ rows, onChange }) {
  const add = () => onChange([...rows, { weight_label: "", price: 0 }]);
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`weight-row-${i}`}>
          <input value={r.weight_label} onChange={(e) => update(i, { weight_label: e.target.value })}
            placeholder="e.g. 1kg"
            className="flex-1 rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`weight-label-${i}`} />
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm">₹</span>
            <input type="number" value={r.price} onChange={(e) => update(i, { price: e.target.value })}
              className="w-32 rounded-xl bg-white border border-warm pl-7 pr-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
              data-testid={`weight-price-${i}`} />
          </div>
          <button type="button" onClick={() => remove(i)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`weight-del-${i}`}>
            <Trash2 className="w-4 h-4 text-rose-500" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
        className="text-xs font-bold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 uppercase tracking-widest"
        data-testid="weight-add">
        <Plus className="w-3 h-3" strokeWidth={2.5} /> Add weight
      </button>
      <div className="flex flex-wrap gap-2 mt-1">
        {["0.5kg", "1kg", "1.5kg", "2kg"].map((w) => (
          <button key={w} type="button"
            onClick={() => onChange([...rows, { weight_label: w, price: 0 }])}
            className="text-[11px] rounded-full border border-warm bg-white px-3 py-1 hover:border-rose-400">
            + {w}
          </button>
        ))}
      </div>
    </div>
  );
}

function VariantsTable({ rows, onChange }) {
  const add = () => onChange([...rows, { option_label: "", price: 0 }]);
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`variant-row-${i}`}>
          <input value={r.option_label} onChange={(e) => update(i, { option_label: e.target.value })}
            placeholder="e.g. Small / Red"
            className="flex-1 rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`variant-label-${i}`} />
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40 text-sm">₹</span>
            <input type="number" value={r.price} onChange={(e) => update(i, { price: e.target.value })}
              className="w-32 rounded-xl bg-white border border-warm pl-7 pr-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
              data-testid={`variant-price-${i}`} />
          </div>
          <button type="button" onClick={() => remove(i)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`variant-del-${i}`}>
            <Trash2 className="w-4 h-4 text-rose-500" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
        className="text-xs font-bold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 uppercase tracking-widest"
        data-testid="variant-add">
        <Plus className="w-3 h-3" strokeWidth={2.5} /> Add variant
      </button>
    </div>
  );
}

function FlavorsEditor({ flavors, onAdd, onRemove, newFlavor, setNewFlavor }) {
  const commit = () => {
    const v = (newFlavor || "").trim();
    if (!v) return;
    onAdd(v);
    setNewFlavor("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="flavors-list">
        {flavors.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 text-xs font-semibold"
            data-testid={`flavor-chip-${i}`}>
            {f}
            <button type="button" onClick={() => onRemove(i)} className="hover:text-rose-900" data-testid={`flavor-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {flavors.length === 0 && (
          <span className="text-xs text-charcoal/40">No flavors added yet.</span>
        )}
      </div>
      <div className="flex gap-2">
        <input value={newFlavor} onChange={(e) => setNewFlavor(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder="e.g. Chocolate, Vanilla, Butterscotch"
          className="flex-1 rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
          data-testid="flavor-input" />
        <button type="button" onClick={commit}
          className="rounded-full px-4 py-2.5 text-xs font-bold bg-charcoal text-white"
          data-testid="flavor-add">
          Add
        </button>
      </div>
    </div>
  );
}

function MultiImageUpload({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post(`/files/upload?folder=products`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      }
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const removeAt = (i) => onChange(images.filter((_, j) => j !== i));
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...images];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="images-grid">
        {images.map((url, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border-2 border-warm bg-linen-2 group"
            data-testid={`image-tile-${i}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-1 left-1 bg-rose-500 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full">
                Primary
              </span>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
              {i > 0 && (
                <button type="button" onClick={() => moveUp(i)}
                  className="p-1.5 rounded-full bg-white/90 text-charcoal hover:bg-white"
                  data-testid={`image-primary-${i}`}
                  title="Set as primary">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" onClick={() => removeAt(i)}
                className="p-1.5 rounded-full bg-white/90 text-rose-600 hover:bg-white"
                data-testid={`image-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
          className="aspect-square rounded-xl border-2 border-dashed border-warm hover:border-rose-400 flex flex-col items-center justify-center text-charcoal/50 text-xs gap-1 disabled:opacity-50"
          data-testid="images-add-btn">
          {uploading ? "Uploading…" : (
            <>
              <Plus className="w-5 h-5" strokeWidth={1.75} />
              Add image
            </>
          )}
        </button>
      </div>
      <input ref={ref} type="file" hidden accept="image/*" multiple onChange={handle} />
      {images.length < 3 && (
        <p className="text-xs text-amber-600 mt-2" data-testid="images-warning">
          Please upload at least {3 - images.length} more image{3 - images.length > 1 ? "s" : ""}.
        </p>
      )}
    </div>
  );
}

function RadioRow({ name, value, onChange, options, testKey }) {
  return (
    <div className="grid sm:grid-cols-3 gap-2">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testKey}-${o.value}`}
            className={`text-left rounded-2xl px-4 py-3 border-2 transition-all ${
              active
                ? "border-rose-500 bg-rose-50 shadow-sm"
                : "border-warm bg-white hover:border-charcoal/30"
            }`}
          >
            <p className={`text-sm font-semibold ${active ? "text-charcoal" : "text-charcoal/80"}`}>{o.label}</p>
            <p className="text-[11px] text-charcoal/60 mt-0.5">{o.hint}</p>
          </button>
        );
      })}
    </div>
  );
}

function PincodeListEditor({ pincodes, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = (input || "").replace(/\D/g, "").slice(0, 6);
    if (!/^\d{6}$/.test(v)) { toast.error("Enter a 6-digit pincode"); return; }
    if (pincodes.includes(v)) { toast.error("Pincode already added"); return; }
    onChange([...pincodes, v]);
    setInput("");
  };
  const remove = (p) => onChange(pincodes.filter((x) => x !== p));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2" data-testid="pf-pincode-list">
        {pincodes.length === 0 && (
          <span className="text-xs text-charcoal/40">No pincodes added yet.</span>
        )}
        {pincodes.map((p) => (
          <span key={p} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 text-xs font-semibold"
            data-testid={`pf-pincode-chip-${p}`}>
            {p}
            <button type="button" onClick={() => remove(p)} className="hover:text-rose-900" data-testid={`pf-pincode-remove-${p}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="6-digit pincode"
          maxLength={6}
          inputMode="numeric"
          className="w-40 rounded-xl bg-white border border-warm px-3 py-2 focus:outline-none focus:border-rose-400 text-sm font-mono"
          data-testid="pf-pincode-input" />
        <button type="button" onClick={add}
          className="rounded-full px-4 py-2 text-xs font-bold bg-charcoal text-white"
          data-testid="pf-pincode-add">
          Add
        </button>
      </div>
    </div>
  );
}

function UsersAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setItems(r.data)); }, []);
  return (
    <div className="card-surface overflow-hidden" data-testid="admin-users-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-linen-2">
            <tr>
              <th className="text-left p-4 font-semibold text-charcoal/60 uppercase tracking-widest text-xs">Name</th>
              <th className="text-left p-4 font-semibold text-charcoal/60 uppercase tracking-widest text-xs">Email</th>
              <th className="text-left p-4 font-semibold text-charcoal/60 uppercase tracking-widest text-xs">Role</th>
              <th className="text-left p-4 font-semibold text-charcoal/60 uppercase tracking-widest text-xs">Provider</th>
              <th className="text-left p-4 font-semibold text-charcoal/60 uppercase tracking-widest text-xs">Joined</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-warm hover:bg-linen-2/50">
                <td className="p-4 font-medium text-charcoal">{u.name}</td>
                <td className="p-4 text-charcoal/70">{u.email}</td>
                <td className="p-4">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold border ${u.role === "admin" ? "border-rose-300 text-rose-600 bg-rose-50" : "border-warm text-charcoal/70 bg-white"}`}>{u.role}</span>
                </td>
                <td className="p-4 text-charcoal/70">{u.auth_provider}</td>
                <td className="p-4 text-charcoal/60 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
